import React from "react";
import { MonthlyAdjustmentRespone } from "../../../api/type";
import {
  Document,
  Font,
  Image,
  Page,
  Text,
  View,
  StyleSheet
} from "@react-pdf/renderer";
import dayjs from "dayjs";
import { env } from "../../../config";
import { userCurrentRole } from "../../../utils/common-functions";

interface AdjustmentReportPrintComponentProps {
  data: MonthlyAdjustmentRespone;
}

Font.register({
  family: "Noto Serif Bengali",
  src: "/fonts/NotoSerifBengali-Regular.ttf"
});

const styles = StyleSheet.create({
  page: {
    padding: "0.75in",
    fontFamily: "Noto Serif Bengali"
  },
  organizationLogo: {
    width: "70px",
    objectFit: "contain",
    alignSelf: "center"
  },
  organizationName: {
    fontSize: 15,
    fontWeight: "bold",
    textAlign: "center"
  },
  header: {
    width: "100%",
    marginTop: 3,
    marginBottom: 10
  },
  headerContent: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    textAlign: "left",
    fontSize: 11
  },
  headerContentDetails: {
    width: "50%",
    border: "1px solid #dfdfdf",
    paddingHorizontal: 5,
    paddingVertical: 1
  },
  table: {
    width: "100%",
    borderLeft: "1px solid #dfdfdf",
    borderRight: "1px solid #dfdfdf"
  },
  tableHeader: {
    borderTop: "1px solid #dfdfdf",
    backgroundColor: "#EBECF0",
    fontWeight: "semibold"
  },
  row: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    textAlign: "center",
    border: "1px solid #dfdfdf",
    fontSize: 11,
    backgroundColor: "#EBECF0"
  },
  serial: {
    width: "10%",
    textAlign: "left",
    paddingLeft: 4,
    borderRight: "1px solid #dfdfdf"
  },
  productName: {
    width: "58%",
    textAlign: "left",
    paddingHorizontal: 5,
    borderRight: "1px solid #dfdfdf"
  },
  increment: {
    width: "16%",
    borderRight: "1px solid #dfdfdf"
  },
  decrement: {
    width: "16%"
  },
  expandRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    textAlign: "left",
    fontSize: 8,
    marginBottom: 2
  },
  no: {
    width: "10%",
    paddingLeft: 5
  },
  locationName: {
    width: "35%",
    paddingHorizontal: 5
  },
  quantity: {
    width: "14%"
  },
  operationBy: {
    width: "23%"
  },
  dateTime: {
    width: "18%",
    paddingRight: 5
  },
  note: {
    fontSize: 8,
    fontWeight: "normal",
    width: "100%"
  },
  signature: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    fontSize: 11,
    fontWeight: "semibold",
    marginTop: 50
  },
  footer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    position: "absolute",
    fontSize: 6,
    color: "#666666",
    bottom: "0.50in",
    left: "0.75in",
    right: "0.75in",
    borderTop: "1px solid #000"
  },
  backgroundImage: {
    position: "absolute",
    top: "45%",
    width: "200px",
    opacity: 0.05,
    objectFit: "contain",
    alignSelf: "center"
  }
});

export const AdjustmentReportPrintComponent: React.FC<
  AdjustmentReportPrintComponentProps
> = (props: AdjustmentReportPrintComponentProps) => {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Image style={styles.backgroundImage} src={env.reportLogo} fixed />
        <View>
          <Image style={styles.organizationLogo} src={env.organizationLogo} />
          <Text style={styles.organizationName}>
            {env.organizationFullName}
          </Text>
          <View style={styles.header}>
            <View style={styles.headerContent}>
              <Text style={styles.headerContentDetails}>
                Report Type : Adjustment Report
              </Text>
              <Text style={styles.headerContentDetails}>
                Location Name : {props.data.data.location.name}
              </Text>
            </View>
            <View style={styles.headerContent}>
              <Text style={styles.headerContentDetails}>
                Report Month :{" "}
                {dayjs(props.data.data.month).format("MMMM, YYYY")}
              </Text>
              <Text style={styles.headerContentDetails}>
                Printout Date :{"  "}
                {dayjs().format("DD MMMM YYYY, hh:mm A")}
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.table}>
          <View style={[styles.tableHeader, styles.row]}>
            <Text style={styles.serial}>Serial</Text>
            <Text style={styles.productName}>Product Name</Text>
            <Text style={styles.increment}>Increment</Text>
            <Text style={styles.decrement}>Decrement</Text>
          </View>
          {props.data.data.productOperationSummary.map(
            (operationDetails, i) => (
              <View key={i}>
                <View style={styles.row} key={i} wrap={false}>
                  <Text style={styles.serial}>{i + 1} </Text>
                  <Text style={styles.productName}>
                    {operationDetails.name + " "}
                  </Text>
                  <Text style={styles.increment}>
                    {operationDetails.inwardCount}
                  </Text>
                  <Text style={styles.decrement}>
                    {operationDetails.outboundCount}
                  </Text>
                </View>
                <View>
                  <View style={styles.expandRow}>
                    <Text style={styles.no}>No.</Text>
                    <Text style={styles.locationName}>Location Name</Text>
                    <Text style={styles.quantity}>Quantity</Text>
                    <Text style={styles.operationBy}>Operation By</Text>
                    <Text style={styles.dateTime}>Date / Time</Text>
                  </View>
                  {operationDetails.operations.map((operation, id) =>
                    operation.notes &&
                    operation.notes !== null &&
                    operation.notes !== "" ? (
                      <View>
                        <View style={styles.expandRow} key={id} wrap={false}>
                          <Text style={styles.no}>{id + 1} </Text>
                          <Text style={styles.locationName}>
                            {operation.originLocation.name}
                          </Text>
                          <Text style={styles.quantity}>
                            {operation.quantity}
                          </Text>
                          <Text style={styles.operationBy}>
                            {operation.createdBy.name}
                          </Text>
                          <Text style={styles.dateTime}>
                            {dayjs(operation.createdAt).format(
                              "DD MMM YY, hh:mm A"
                            )}
                          </Text>
                        </View>

                        <View>
                          <Text
                            style={[
                              {
                                fontSize: 8,
                                fontWeight: "semibold",
                                borderBottom: "1px solid #dfdfdf",
                                paddingHorizontal: 5,
                                paddingBottom: 2
                              }
                            ]}
                          >
                            Note :{" "}
                            <Text style={styles.note}>{operation.notes}</Text>
                          </Text>
                        </View>
                      </View>
                    ) : (
                      <View style={styles.expandRow} key={id} wrap={false}>
                        <Text style={styles.no}>{id + 1} </Text>
                        <Text style={styles.locationName}>
                          {operation.originLocation.name}
                        </Text>
                        <Text style={styles.quantity}>
                          {operation.quantity}
                        </Text>
                        <Text style={styles.operationBy}>
                          {operation.createdBy.name}
                        </Text>
                        <Text style={styles.dateTime}>
                          {dayjs(operation.createdAt).format(
                            "DD MMM YY, hh:mm A"
                          )}
                        </Text>
                      </View>
                    )
                  )}
                </View>
              </View>
            )
          )}
        </View>

        <View style={styles.signature}>
          <View>
            <Text>Verifed By,</Text>
          </View>
          <View>
            <Text>Authorized By,</Text>
          </View>
        </View>
        <View style={styles.footer} fixed>
          <View>
            <Text>Powered By Bindulogic Limited</Text>
          </View>
          <View>
            <Text
              render={({ pageNumber, totalPages }) =>
                `Page ${pageNumber} of ${totalPages}`
              }
            />
          </View>
          <View>
            <Text>
              Printed By: {userCurrentRole() || "Unknown"},{" "}
              {dayjs().format("DD MMM YYYY, hh:mm A")}
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};
